import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import MuiAutocomplete, {
  AutocompleteProps as MuiAutocompleteProps,
} from "@mui/material/Autocomplete";
import { TextField } from "./Textfield";
import { Tooltip, TooltipProps } from "./Tooltip";
import { CONTACT_SUBJECT_OPTIONS } from "@/components/views/BottomNavBar/Contact";

export interface AutocompleteOption {
  key: string | number;
  value: string | number;
  display?: string;
}

export type LocationAutocompleteOption = {
  key: string;
  value: string;
  display: string;
  location_type: string;
  is_active: boolean;
};

export type AutocompleteProps = Omit<
  MuiAutocompleteProps<AutocompleteOption, boolean, boolean, boolean>,
  | "renderInput"
  | "groupBy"
  | "getOptionLabel"
  | "getOptionDisabled"
  | "onChange"
> & {
  componentName: string;
  inputLabel: string;
  options:
    | AutocompleteOption[]
    | LocationAutocompleteOption[]
    | typeof CONTACT_SUBJECT_OPTIONS;
  value:
    | AutocompleteOption
    | AutocompleteOption[]
    | LocationAutocompleteOption[]
    | null;
  onChange: (
    event: React.SyntheticEvent<Element, Event>,
    value:
      | AutocompleteOption
      | AutocompleteOption[]
      | LocationAutocompleteOption[]
      | null,
  ) => void;
  groupBy?: (option: AutocompleteOption | LocationAutocompleteOption) => string;
  getOptionLabel?: (
    option: AutocompleteOption | LocationAutocompleteOption | string,
  ) => string;
  getOptionDisabled?: (
    option: AutocompleteOption | LocationAutocompleteOption,
  ) => boolean;
  boxSx?: Record<string, any>;
  autocompleteSx?: Record<string, any>;
  textfieldSx?: Record<string, any>;
  tooltipMessage?: TooltipProps["tooltipMessage"];
  readMoreLink?: TooltipProps["readMoreLink"];
  renderInput?: (params: any) => React.ReactNode;
  required?: boolean;
  renderCheckMarks?: boolean;
};

export const Autocomplete = ({
  componentName,
  inputLabel,
  options,
  value,
  onChange,
  boxSx,
  autocompleteSx,
  textfieldSx,
  tooltipMessage,
  readMoreLink,
  renderInput,
  required = false,
  renderCheckMarks = false,
  ...props
}: AutocompleteProps) => {
  const valueProp = typeof value === "undefined" ? null : value;

  const enhancedOptions = useMemo(() => {
    if (!Array.isArray(options)) return [];

    if (
      required &&
      options.length > 0 &&
      options[0].value === "Select a subject"
    ) {
      return [
        {
          ...options[0],
          value: `${options[0].value} *`,
        },
        ...options.slice(1),
      ];
    }

    return options;
  }, [options, required]);

  const AutocompleteContent = (
    <MuiAutocomplete
      {...props}
      fullWidth
      data-testid={`${componentName}-autocomplete`}
      value={valueProp}
      options={enhancedOptions}
      onChange={(e, newValue) => {
        if (Array.isArray(newValue)) {
          // Flatten in case of nested arrays and remove `string` values
          const filteredValues:
            | AutocompleteOption[]
            | LocationAutocompleteOption[] = newValue
            .flat()
            .filter(
              (item): item is AutocompleteOption => typeof item !== "string",
            );

          onChange(
            e,
            filteredValues.length > 0
              ? filteredValues // if filteredValues is empty, return an empty array or null
              : props.multiple
                ? []
                : null,
          );
        } else if (typeof newValue !== "string") {
          onChange(e, newValue ?? (props.multiple ? [] : null));
        }
      }}
      sx={{
        mt: "2em",
        ...autocompleteSx,
      }}
      getOptionLabel={option => {
        if (typeof option === "string") return option;

        const isFirstOption =
          option.key === 0 && option.value === "Select a subject";

        return isFirstOption && required
          ? `${option.display || option.value} *`
          : option.display?.toString() || option.value?.toString() || "";
      }}
      limitTags={1}
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          componentName={componentName}
          label={inputLabel}
          placeholder={inputLabel}
          sx={{
            backgroundColor: props.disabled
              ? "grayscale.disabled"
              : "transparent",
            ...textfieldSx,
          }}
        />
      )}
      isOptionEqualToValue={(option, value) =>
        option.key === value.key && option.value === value.value
      }
      renderOption={(props, option, { selected }) => {
        const { key, ...rest } = props;

        return (
          <li key={key} {...rest}>
            <Box
              component="span"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span>
                {typeof option === "string"
                  ? option
                  : (option.display ?? option.value)}
              </span>
              {renderCheckMarks && selected && (
                <span style={{ color: "green" }}>✔️</span>
              )}
            </Box>
          </li>
        );
      }}
      loading={props.loading}
      loadingText="Loading..."
    />
  );

  return (
    <Box
      data-testid={`${componentName}-autocomplete-box`}
      sx={{
        mt: 2,
        width: "100%",
        ...boxSx,
      }}
    >
      {tooltipMessage ? (
        <Tooltip
          componentName={componentName}
          tooltipMessage={tooltipMessage}
          readMoreLink={readMoreLink}
        >
          {AutocompleteContent}
        </Tooltip>
      ) : (
        AutocompleteContent
      )}
    </Box>
  );
};
